import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunity } from '@/contexts/CommunityContext';
import { UserRole } from '@/contexts/CommunityContext';
import { CommunityMemberStatus } from '@/types/community';
import { toast } from '@/components/ui/use-toast';
import { COMMUNITY_MEMBERS_QUERY_KEY } from './useUserProfiles';

export interface CommunityMember {
  id: string;
  user_id: string;
  community_id: string;
  role_name: UserRole;
  joined_at: string;
  status: CommunityMemberStatus;
  // UI only properties
  displayName?: string;
  email?: string;
}

export const useCommunityMembers = (communityId?: string) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentCommunity, isAllowedTo } = useCommunity();
  
  // Use provided communityId or fall back to currentCommunity.id
  const targetCommunityId = communityId || currentCommunity.id;
  
  const canManageMembersQuery = useQuery({
    queryKey: ['canManageMembers', user?.id, targetCommunityId],
    queryFn: async () => {
      if (!user || !targetCommunityId) return false;
      
      try {
        return await isAllowedTo('manage_users');
      } catch (err) {
        console.error('Error checking member management permission:', err);
        return false;
      }
    },
    enabled: !!user && !!targetCommunityId,
  });
  
  const membersQuery = useQuery({
    queryKey: [COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId],
    queryFn: async () => {
      if (!targetCommunityId) return [];
      
      // Check if user can view all members
      const canManageMembers = canManageMembersQuery.data;
      
      if (!canManageMembers && (!user || user.id === undefined)) {
        // If not an admin and not logged in, return empty list
        return [];
      }
      
      try {
        let query = supabase
          .from('community_members' as any)
          .select('*')
          .eq('community_id', targetCommunityId);
        
        if (!canManageMembers) {
          // If not an admin, only show the current user's membership
          query = query.eq('user_id', user!.id);
        }
        
        const { data, error } = await query;
        
        if (error) {
          throw error;
        }
        
        return data as unknown as CommunityMember[];
      } catch (error) {
        console.error('Error fetching community members:', error);
        throw error;
      }
    },
    enabled: !!targetCommunityId && canManageMembersQuery.isSuccess,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 3
  });
  
  const addMember = useMutation({
    mutationFn: async ({ userId, roleName }: { userId: string, roleName: UserRole }) => {
      if (!targetCommunityId) {
        throw new Error('Community ID is required');
      }
      
      // Check if user can manage members
      const canManageMembers = await isAllowedTo('manage_users');
      if (!canManageMembers) {
        throw new Error('You do not have permission to add members');
      }
      
      const { data, error } = await supabase
        .from('community_members' as any)
        .insert({
          user_id: userId,
          community_id: targetCommunityId,
          role_name: roleName,
          status: 'ACTIVE'
        })
        .select()
        .single();
      
      if (error) {
        if (error.code === '23505') { // Unique violation
          throw new Error('This user is already a member of this community');
        }
        throw error;
      }
      
      return data as unknown as CommunityMember;
    },
    onSuccess: () => {
      // Invalidate both query keys to ensure data consistency
      queryClient.invalidateQueries({ queryKey: [COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId] });
      toast({
        title: 'Member added',
        description: 'The user has been added to the community',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  });
  
  const updateMemberRole = useMutation({
    mutationFn: async ({ memberId, roleName }: { memberId: string, roleName: UserRole }) => {
      // Check if user can manage members
      const canManageMembers = await isAllowedTo('manage_users');
      if (!canManageMembers) {
        throw new Error('You do not have permission to update member roles');
      }
      
      const { data, error } = await supabase
        .from('community_members' as any)
        .update({ role_name: roleName })
        .eq('id', memberId)
        .eq('community_id', targetCommunityId) // Extra safety check
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      return data as unknown as CommunityMember;
    },
    onMutate: async ({ memberId, roleName }) => {
      // Optimistic update
      // 1. Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId] });
      
      // 2. Snapshot the previous value
      const previousMembers = queryClient.getQueryData([COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId]);
      
      // 3. Optimistically update to the new value
      queryClient.setQueryData(
        [COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId], 
        (old: any) => {
          if (!old) return old;
          
          // Create a new array with the updated role
          const updatedMembers = old.map((member: any) => 
            member.id === memberId ? { ...member, role_name: roleName } : member
          );
          
          // Return the updated array without changing the order
          return updatedMembers;
        }
      );
      
      return { previousMembers };
    },
    onSuccess: () => {
      // Invalidate both query keys to ensure data consistency
      queryClient.invalidateQueries({ queryKey: [COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId] });
      toast({
        title: 'Role updated',
        description: 'The member\'s role has been updated',
      });
    },
    onError: (error: Error, variables, context) => {
      // Restore previous members data on error
      if (context?.previousMembers) {
        queryClient.setQueryData([COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId], context.previousMembers);
      }
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  });
  
  const blockMember = useMutation({
    mutationFn: async (memberId: string) => {
      // Check if user can manage members
      const canManageMembers = await isAllowedTo('manage_users');
      if (!canManageMembers) {
        throw new Error('You do not have permission to block members');
      }
      
      const { data, error } = await supabase
        .from('community_members' as any)
        .update({ status: 'BLOCKED' })
        .eq('id', memberId)
        .eq('community_id', targetCommunityId) // Extra safety check
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      return data as unknown as CommunityMember;
    },
    onMutate: async (memberId) => {
      // Optimistic update
      // 1. Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId] });
      
      // 2. Snapshot the previous value
      const previousMembers = queryClient.getQueryData([COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId]);
      
      // 3. Optimistically update to the new value
      queryClient.setQueryData(
        [COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId], 
        (old: any) => {
          if (!old) return old;
          
          // Update the member status to BLOCKED
          const updatedMembers = old.map((member: any) => 
            member.id === memberId ? { ...member, status: 'BLOCKED' } : member
          );
          
          return updatedMembers;
        }
      );
      
      return { previousMembers };
    },
    onSuccess: () => {
      // Invalidate both query keys to ensure data consistency
      queryClient.invalidateQueries({ queryKey: [COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId] });
      toast({
        title: 'Member blocked',
        description: 'The member has been blocked from the community',
      });
    },
    onError: (error: Error, variables, context) => {
      // Restore previous members data on error
      if (context?.previousMembers) {
        queryClient.setQueryData([COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId], context.previousMembers);
      }
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  const unblockMember = useMutation({
    mutationFn: async (memberId: string) => {
      // Check if user can manage members
      const canManageMembers = await isAllowedTo('manage_users');
      if (!canManageMembers) {
        throw new Error('You do not have permission to unblock members');
      }
      
      const { data, error } = await supabase
        .from('community_members' as any)
        .update({ status: 'ACTIVE' })
        .eq('id', memberId)
        .eq('community_id', targetCommunityId) // Extra safety check
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      return data as unknown as CommunityMember;
    },
    onMutate: async (memberId) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: [COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId] });
      
      const previousMembers = queryClient.getQueryData([COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId]);
      
      queryClient.setQueryData(
        [COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId], 
        (old: any) => {
          if (!old) return old;
          
          const updatedMembers = old.map((member: any) => 
            member.id === memberId ? { ...member, status: 'ACTIVE' } : member
          );
          
          return updatedMembers;
        }
      );
      
      return { previousMembers };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId] });
      toast({
        title: 'Member unblocked',
        description: 'The member has been unblocked and can access the community again',
      });
    },
    onError: (error: Error, variables, context) => {
      if (context?.previousMembers) {
        queryClient.setQueryData([COMMUNITY_MEMBERS_QUERY_KEY, targetCommunityId], context.previousMembers);
      }
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  });
  
  // Keep the old removeMember for backwards compatibility, but now it blocks instead
  const removeMember = blockMember;
  
  const joinCommunity = useMutation({
    mutationFn: async () => {
      if (!user) {
        throw new Error('You must be logged in to join a community');
      }
      
      if (!targetCommunityId) {
        throw new Error('Community ID is required');
      }
      
      // Check if community is public (private communities require invitation)
      const { data: communityData, error: communityError } = await supabase
        .from('communities')
        .select('*')
        .eq('id', targetCommunityId)
        .single();
        
      if (communityError) {
        throw communityError;
      }
      
      // Safely access privacy_type with type casting
      const privacyType = (communityData as any).privacy_type || 'public';
      
      if (privacyType !== 'public') {
        throw new Error('This is a private community. You need an invitation to join.');
      }

      // Check if user was previously blocked
      const { data: existingMember } = await supabase
        .from('community_members' as any)
        .select('status')
        .eq('user_id', user.id)
        .eq('community_id', targetCommunityId)
        .maybeSingle();

      if (existingMember?.status === 'BLOCKED') {
        throw new Error('You have been blocked from this community. Please contact an administrator for assistance.');
      }
      
      // For public communities, users can join with default 'user' role
      const { data, error } = await supabase
        .from('community_members' as any)
        .insert({
          user_id: user.id,
          community_id: targetCommunityId,
          role_name: 'user',
          status: 'ACTIVE'
        })
        .select()
        .single();
      
      if (error) {
        if (error.code === '23505') { // Unique violation
          throw new Error('You are already a member of this community');
        }
        throw error;
      }
      
      return data as unknown as CommunityMember;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communityMembers', targetCommunityId] });
      toast({
        title: 'Joined community',
        description: 'You have successfully joined the community',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  });
  
  const leaveCommunity = useMutation({
    mutationFn: async () => {
      if (!user) {
        throw new Error('You must be logged in to leave a community');
      }
      
      if (!targetCommunityId) {
        throw new Error('Community ID is required');
      }
      
      // Users can leave any community they are part of
      const { error } = await supabase
        .from('community_members' as any)
        .delete()
        .eq('user_id', user.id)
        .eq('community_id', targetCommunityId);
      
      if (error) {
        throw error;
      }
      
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communityMembers', targetCommunityId] });
      toast({
        title: 'Left community',
        description: 'You have successfully left the community',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  });
  
  return {
    members: membersQuery.data || [],
    isLoading: membersQuery.isLoading,
    error: membersQuery.error,
    canManageMembers: canManageMembersQuery.data,
    isLoadingPermissions: canManageMembersQuery.isLoading,
    addMember,
    updateMemberRole,
    removeMember, // This now blocks instead of deletes
    blockMember,
    unblockMember,
    joinCommunity,
    leaveCommunity
  };
};
